import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabaseClient';

/**
 * Auth + profile/role context.
 *
 * After Phase 21 (P1 role model), every authenticated user is expected to
 * have a row in `public.profiles` with role in ('admin','crew_lead'). The
 * 007 migration auto-creates these rows + backfills existing users to admin.
 *
 * Until the profile fetch completes, callers should rely on `loading`. If
 * the user IS authenticated but the profile fetch fails (e.g. RLS error,
 * 007 not yet applied to Production), we default to `role: 'crew_lead'`
 * — the LESS privileged of the two roles — so a misconfigured production
 * deploy fails CLOSED, not open.
 */
const AuthContext = createContext({
  user: null,
  session: null,
  profile: null,
  role: null,
  tier: 'foreman',
  isAdmin: false,
  isCrewLead: false,
  loading: true,
  profileLoading: true,
  profileError: null,
  signIn: async () => ({ error: null }),
  signOut: async () => {},
  refreshProfile: async () => {},
  resetPasswordForEmail: async () => ({ error: null }),
  updatePassword: async () => ({ error: null }),
  isPasswordRecovery: false,
});

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState(null);
  // Password recovery state - set to true when Supabase fires PASSWORD_RECOVERY
  // (i.e. user just clicked the emailed reset link). Consumed by ResetPasswordPage
  // to gate the "set new password" form.
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);

  const loadProfileFor = useCallback(async (uid) => {
    if (!uid) {
      setProfile(null);
      setProfileLoading(false);
      setProfileError(null);
      return;
    }
    setProfileLoading(true);
    setProfileError(null);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, role, tier, crew_id, full_name, created_at, updated_at, onboarding_completed_at')
        .eq('id', uid)
        .maybeSingle();
      if (error) throw error;
      setProfile(data || null);
    } catch (e) {
      // Fail CLOSED on ANY profile-load error.
      //
      // Once SQL migration 007 is applied, every authenticated user has a
      // `profiles` row (created by the on-signup trigger + one-time backfill),
      // so this catch should only fire for genuine faults: RLS denial, a
      // network failure, a stale schema cache, or a missing table if 007 was
      // never run. In every one of those cases we must NOT grant privileges.
      //
      // Historical note: a previous version synthesized an `admin` profile
      // when the error *looked like* a missing table (42P01 / PGRST205 /
      // "schema cache" / "does not exist"). That fails OPEN \u2014 a signup or a
      // transient error whose message merely contained "does not exist" would
      // become admin with full data access. It was removed. If 007 is not yet
      // applied, the correct behavior is to block admin surfaces, not unlock
      // them; the profile stays null so `role` defaults to the less-privileged
      // 'crew_lead' (see the derived `role` below).
      setProfile(null);
      setProfileError(e?.message || 'Failed to load profile');
    } finally {
      setProfileLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    // Restore existing session on mount
    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!mounted) return;
        const s = data.session ?? null;
        setSession(s);
        setUser(s?.user ?? null);
        if (s?.user?.id) loadProfileFor(s.user.id);
        else {
          setProfile(null);
          setProfileLoading(false);
        }
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    // Subscribe to auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      // PASSWORD_RECOVERY fires when the user lands on the app from the emailed
      // reset link. Supabase automatically materializes a temporary session
      // from the URL fragment so `updateUser({ password })` will succeed.
      if (event === 'PASSWORD_RECOVERY') {
        setIsPasswordRecovery(true);
      }
      // Any subsequent SIGNED_IN or SIGNED_OUT clears the recovery flag.
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'USER_UPDATED') {
        // Do NOT clear immediately on SIGNED_IN if we're still in a recovery
        // flow — Supabase fires SIGNED_IN right after PASSWORD_RECOVERY when it
        // materializes the temp session. We only clear once the ResetPasswordPage
        // completes its updateUser call (which fires USER_UPDATED).
        if (event === 'USER_UPDATED' || event === 'SIGNED_OUT') {
          setIsPasswordRecovery(false);
        }
      }
      setSession(newSession ?? null);
      setUser(newSession?.user ?? null);
      setLoading(false);
      if (newSession?.user?.id) {
        loadProfileFor(newSession.user.id);
      } else {
        setProfile(null);
        setProfileLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription?.unsubscribe?.();
    };
  }, [loadProfileFor]);

  const signIn = async ({ email, password }) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) return { error, data: null };
      return { error: null, data };
    } catch (e) {
      // Defensive: surface a clean error in case of network or fetch-wrapper failure
      return {
        error: { message: e?.message || 'Authentication failed. Please try again.' },
        data: null,
      };
    }
  };

  /**
   * Self-serve contractor signup. Passes account_type='contractor' + business
   * details in user_metadata; the _handle_new_auth_user trigger (039) reads
   * these to provision an admin tenant with founding-member defaults and a
   * seeded company_profile. No card is taken (pre-launch).
   */
  const signUp = async ({ email, password, fullName, businessName, tier, phone, marketingOptIn, attribution }) => {
    try {
      // Attribution (utm tags, referrer, landing page) rides in the signup
      // metadata; the database trigger records the signup as a lead with
      // its source (K.O. Command, migration 071). Only non-empty values.
      const attr = Object.fromEntries(
        Object.entries(attribution || {}).filter(([, v]) => typeof v === 'string' && v.trim())
      );
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/dashboard`,
          data: {
            account_type: 'contractor',
            full_name: fullName || '',
            business_name: businessName || '',
            tier: tier || 'foreman',
            phone: phone || '',
            marketing_opt_in: !!marketingOptIn,
            ...attr,
          },
        },
      });
      if (error) return { error, data: null };
      return { error: null, data };
    } catch (e) {
      return {
        error: { message: e?.message || 'Sign up failed. Please try again.' },
        data: null,
      };
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setUser(null);
    setProfile(null);
    setProfileError(null);
    setIsPasswordRecovery(false);
  };

  /**
   * Send a password-reset email. Supabase emails a magic link that, when
   * clicked, drops the user on `${origin}/reset-password` with a temporary
   * recovery session in the URL fragment.
   */
  const resetPasswordForEmail = async (email) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) return { error };
      return { error: null };
    } catch (e) {
      return {
        error: { message: e?.message || 'Could not send reset email. Please try again.' },
      };
    }
  };

  /**
   * Update the current user's password. Works in two contexts:
   *   1. In-app "Change Password" (already-signed-in user).
   *   2. "Reset Password" page — Supabase materialized a temporary recovery
   *      session from the emailed link, so updateUser succeeds.
   */
  const updatePassword = async (newPassword) => {
    try {
      const { data, error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) return { error, data: null };
      return { error: null, data };
    } catch (e) {
      return {
        error: { message: e?.message || 'Could not update password. Please try again.' },
        data: null,
      };
    }
  };

  const refreshProfile = useCallback(
    () => loadProfileFor(user?.id),
    [loadProfileFor, user?.id]
  );

  // Derived role helpers.
  // Fails CLOSED: when no profile, isAdmin = false (treat as crew_lead).
  const role = profile?.role || (user ? 'crew_lead' : null);
  const isAdmin = role === 'admin';
  const isCrewLead = role === 'crew_lead';
  // Subscription tier (migration 010): 'tradesman' | 'foreman' | 'boss'.
  // Drives white-label / feature gating in the UI (not a security boundary —
  // real enforcement lives server-side). Defaults to the trial tier 'foreman'
  // when the profile hasn't loaded a tier, matching the DB column default.
  const tier = profile?.tier || 'foreman';

  // A contractor (admin) who hasn't finished the onboarding wizard yet.
  // Non-admins never see onboarding. Fails safe: if the profile hasn't loaded,
  // needsOnboarding is false (don't trap anyone in the wizard on a load error).
  const needsOnboarding =
    isAdmin && !!profile && !profile.onboarding_completed_at;

  const value = useMemo(
    () => ({
      user,
      session,
      profile,
      role,
      tier,
      isAdmin,
      isCrewLead,
      needsOnboarding,
      signUp,
      // Legacy compat: previous code used `isOwner: !!user`. Anything still
      // reading `isOwner` should be migrated to `isAdmin`. We keep the alias
      // wired to isAdmin (NOT to !!user) so we don't accidentally re-leak
      // admin UI to crew_leads.
      isOwner: isAdmin,
      loading,
      profileLoading,
      profileError,
      signIn,
      signOut,
      refreshProfile,
      resetPasswordForEmail,
      updatePassword,
      isPasswordRecovery,
    }),
    [user, session, profile, role, tier, isAdmin, isCrewLead, needsOnboarding, loading, profileLoading, profileError, refreshProfile, isPasswordRecovery]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext;
